"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Activity,
  Sparkles,
  Crown,
  Mic,
  ArrowRight,
  Target,
  Layers,
  Compass,
  Award,
  Briefcase,
  Building2,
  Lock,
  GitBranch,
  ShieldCheck,
  TrendingUp,
  Users,
  Clock,
  Zap,
  Cpu,
  Globe2,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const DOMAINS = [
  {
    icon: Layers,
    title: "Operational Excellence System Implementation",
    body: "Building the infrastructure that produces consistent, measurable outcomes — protocols, checklists, decision frameworks, accountability structures, and the organizational discipline to sustain them. Operational excellence approached as a continuous practice of diagnosing, prescribing, implementing, and measuring that produces compound improvement over time.",
    accent: "red",
  },
  {
    icon: Building2,
    title: "Governance Transformation",
    body: "Redesigning governance structures to support execution — not theoretical frameworks, but operational change that produces measurable improvement in how organizations govern, decide, and perform. Four non-negotiable pillars: digital infrastructure, data intelligence, process reform, and stakeholder engagement. Only when all four operate in concert does governance transformation achieve its full potential.",
    accent: "purple",
  },
  {
    icon: Cpu,
    title: "Digital Transformation Strategy",
    body: "Implementing technology infrastructure that enables operational excellence. Platform selection and architecture, data infrastructure design, AI and machine learning integration, security architecture, and operational technology deployment — each element designed to serve the organization&apos;s operational objectives rather than constrain them.",
    accent: "cyan",
  },
  {
    icon: ShieldCheck,
    title: "Crisis-Ready Operational Design",
    body: "Building organizational resilience through protocol-driven preparedness. Systematic risk identification, evidence-based triage, protocol-driven response, and continuous monitoring. Crisis-ready design integrates resilience into the organization&apos;s operational DNA — protocols, checklists, decision frameworks, and escalation mechanisms that activate automatically when disruption occurs.",
    accent: "emerald",
  },
  {
    icon: Activity,
    title: "Organizational Diagnosis and Restructuring",
    body: "The clinical method applied to organizational structure — diagnosing structural inefficiencies, prescribing structural interventions, and implementing change with operational continuity maintained throughout. Differential diagnosis, evidence-based reasoning, and protocol-driven treatment planning applied to the full organizational landscape.",
    accent: "amber-gold",
  },
];

const STAGES = [
  { num: "01", title: "Intelligence", body: "A forensic examination of the organizational landscape. The diagnostic phase, non-negotiable. Maps the strategic objectives, operational capabilities, governance structures, execution gaps, cultural dynamics, technology infrastructure, and stakeholder ecosystem with clinical precision." },
  { num: "02", title: "Analysis", body: "Transforms intelligence into diagnosis — identifying root causes of operational gaps, structural vulnerabilities, and the systemic patterns that perpetuate underperformance. Differential diagnosis, evidence-based reasoning, and protocol-driven treatment planning produce the diagnostic report." },
  { num: "03", title: "Strategy", body: "Designs the intervention — the specific operating mechanisms that address the diagnosed gaps. An implementation blueprint: the protocols, checklists, decision frameworks, and accountability structures the organization will deploy. Grounded in verified data." },
  { num: "04", title: "Orchestration", body: "Coordinates implementation across all relevant teams and systems. The orchestration ensures the consulting produces systemic transformation — every team aligned, every protocol deployed, every accountability structure activated. Compartmentalized architecture protects sovereign-level work." },
  { num: "05", title: "Amplification", body: "Scales what works across the organization — taking operating mechanisms that produce verified results and deploying them across the full organizational landscape. Calibrated deployment at the pace the organization can absorb, measuring at every stage." },
  { num: "06", title: "Feedback", body: "Measures outcomes against the metrics established in the strategy stage and adapts the approach based on what the data reveals. Continuous outcome measurement produces verified data on execution speed improvement, operational uptime, governance effectiveness, and organizational resilience." },
];

const MODELS = [
  {
    icon: GitBranch,
    title: "Project Model",
    body: "Defined engagements with specific deliverables — operational assessments, governance transformations, digital infrastructure implementations, crisis-ready design programs, organizational restructurings. The project concludes when the deliverable is verified and the outcome is measured.",
    accent: "red",
  },
  {
    icon: Clock,
    title: "Retainer Model",
    body: "Ongoing operational transformation through regular engagement. Designed for organizations requiring sustained implementation of the operating method as a discipline. Sessions are scheduled weekly or biweekly with diagnostic reviews, system implementation, and outcome measurement embedded throughout.",
    accent: "purple",
  },
  {
    icon: Zap,
    title: "Intensive Model",
    body: "High-density, compressed engagements for organizations facing urgent operational challenges — crisis situations, rapid-scaling requirements, or compressed transformation timelines. Daily engagement, rapid diagnostic cycles, accelerated implementation, continuous measurement.",
    accent: "cyan",
  },
  {
    icon: Lock,
    title: "Introduction Model",
    body: "Private engagements for sovereign-level clients. Designed for discreet, high-stakes operational needs where the engagement itself requires confidentiality. Access by introduction only, ensuring the diagnostic conversation can proceed with the trust and discretion sovereign-level work demands.",
    accent: "emerald",
  },
];

const CLIENTS = [
  { icon: Crown, title: "Governments & Sovereign Institutions", body: "National, state, and local government bodies facing governance transformation, operational modernization, or strategic challenges at national scale.", accent: "red" },
  { icon: ShieldCheck, title: "Defense & Intelligence Agencies", body: "Agencies and institutions requiring sovereign-grade operational consulting, crisis management, and strategic intelligence review. The most stringent confidentiality standards.", accent: "purple" },
  { icon: Award, title: "Fortune-Scale Corporations", body: "Global corporations — technology, financial, energy, pharmaceutical — facing execution gaps, operational complexity, or the need for operational excellence systems.", accent: "cyan" },
  { icon: Globe2, title: "International Organizations", body: "Multilateral institutions and international bodies requiring cross-jurisdictional operational transformation grounded in sovereign-scale experience.", accent: "emerald" },
  { icon: Users, title: "Political Organizations", body: "Sovereign-level political entities facing operational challenges that demand the full weight of sovereign-grade operational expertise.", accent: "amber-gold" },
  { icon: Briefcase, title: "Family Offices & Dynasties", body: "Multi-generational wealth structures requiring operational transformation, governance modernization, and absolute discretion at the highest levels.", accent: "denim" },
];

const GAINS = [
  { icon: Target, title: "Operational Transformation", body: "Verified changes in how the organization actually operates — not a recommendation that sits on a shelf but operational infrastructure that produces consistent results under demanding conditions.", accent: "red" },
  { icon: Layers, title: "Governance Redesign", body: "Institutional redesign that produces measurable improvement in how governance structures support execution, how accountability is maintained, and how decision-making frameworks produce consistent outcomes under pressure.", accent: "purple" },
  { icon: Cpu, title: "Digital Architecture", body: "Technology infrastructure that serves the organization&apos;s operational objectives — zero third-party dependencies where sovereignty demands, proprietary advantage where differentiation matters.", accent: "cyan" },
  { icon: ShieldCheck, title: "Resilience Architecture", body: "Organizations that do not merely survive disruption but maintain operational continuity at levels conventional organizations cannot achieve. The crisis response protocols embedded in operational DNA.", accent: "emerald" },
  { icon: TrendingUp, title: "Measured Improvement", body: "Verified data on execution speed improvement, operational uptime, governance effectiveness, and organizational resilience — measured against benchmarks established during the diagnostic phase.", accent: "amber-gold" },
  { icon: Lock, title: "Discretion", body: "National security level confidentiality governing every consulting engagement. The structural foundation — not a policy preference, the architecture of how every engagement operates.", accent: "denim" },
];

export default function OperationalConsultingPage() {
  return (
    <>
      <PageHero
        icon={Activity}
        overline="Service · Operational Consulting"
        overlineClassName="text-amber-gold"
        title={
          <>
            Sovereign-Scale <span className="text-gradient-premium">Operational Transformation.</span>
          </>
        }
        subtitle="Discreet, high-stakes transformation for governments, leaders, and organizations facing challenges that demand the full weight of sovereign-scale operational expertise. Not management consulting. The implementation of an operating method at the organizational level — producing verified, measurable transformation."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Operational Consulting" }]}
        badge="Transformation, not recommendations"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Sparkles className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Not management consulting. Operational transformation.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The consulting addresses the complete landscape of organizational transformation — operational excellence system implementation, governance transformation, digital transformation strategy, crisis-ready operational design, and organizational diagnosis and restructuring. Each domain is approached with the same clinical method that governs medical practice: precision, evidence, protocol, and accountability.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Most consulting firms produce recommendations — documents that describe what should happen. The operational consulting produces transformation — verified changes in how the organization actually operates. The distinction is structural. A recommendation is a possibility. A transformation is a measured outcome.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The consulting is not available to every organization. It is curated — offered to clients whose challenges justify the full weight of sovereign-scale operational expertise. The diagnostic phase determines whether the engagement is appropriate, whether the organization&apos;s needs align with capabilities, and whether the consulting can produce the verified outcomes that both parties require.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { num: "01", title: "Transformation Over Recommendation", body: "The consulting produces transformation — verified changes in how the organization actually operates. Not a report that sits on a shelf, but operational infrastructure that produces consistent results.", accent: "red" },
              { num: "02", title: "Diagnose Before Prescribing", body: "Forensic precision in mapping the organizational landscape. The diagnostic phase identifies not just symptoms but root causes, not just challenges but structural vulnerabilities.", accent: "purple" },
              { num: "03", title: "Systems Over Decks", body: "The deliverable is an operating system — protocols, checklists, decision frameworks, and the discipline to implement them. Not a strategy document. A system.", accent: "cyan" },
              { num: "04", title: "Outcome Accountability", body: "The consulting is measured by outcomes, not effort. The same accountability that governs medical practice governs every engagement. Results matter more than deliverables.", accent: "emerald" },
              { num: "05", title: "Discretion as Foundation", body: "National security level confidentiality. Zero information leakage. The structural foundation of how every consulting engagement operates — not a policy preference.", accent: "amber-gold" },
            ].map((p, i) => {
              const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
              const a = ACCENT[accents[i]];
              return (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 ring-2 ${a.ring}`}>
                      <span className="text-caption font-bold text-white">{p.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">What Consulting Covers</span>
          </div>
          <h2 className="text-display-md mb-4">Five interconnected domains.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The consulting addresses the complete landscape of organizational transformation — each domain approached with clinical precision, sovereign-scale experience, and outcome accountability.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOMAINS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <d.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{d.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Six-Stage Methodology</span>
          </div>
          <h2 className="text-display-md mb-4">Sovereign Operations Architecture applied to consulting.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each stage is grounded in the clinical method — diagnose before prescribing, evidence over intuition, protocol under pressure, outcome accountability. The stages are sequential in logic but iterative in practice.
          </p>
        </div>
        <div className="space-y-3">
          {STAGES.map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring} group-hover:scale-110 transition-all`}>
                    <span className="text-body-md font-bold text-white">{s.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Briefcase className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Engagement Models</span>
          </div>
          <h2 className="text-display-md mb-4">Four structures, one operating standard.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every consulting engagement follows a structured model designed to match the nature of the challenge, the duration of the relationship, and the operational objectives. All begin with a diagnostic conversation.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {MODELS.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                  <m.icon className="size-6 text-white" />
                </div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{m.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{m.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Crown className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Ideal Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Organizations whose challenges justify sovereign-scale expertise.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The consulting is designed for organizations whose challenges cannot be solved by conventional management consulting — where the stakes are sovereign, the timeline is compressed, and the margin for error is zero.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <c.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{c.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Award className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Clients Gain</span>
          </div>
          <h2 className="text-display-md mb-4">Six structural deliverables.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each grounded in verified operational data, each producing outcomes that persist beyond the engagement itself. The transformation produces measurable improvement in execution speed, operational uptime, governance effectiveness, and organizational resilience.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GAINS.map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <g.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{g.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{g.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <TrendingUp className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Consulting Process Steps</span>
          </div>
          <h2 className="text-display-md mb-4">The consulting process, end to end.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The operational consulting process follows six vertical stages. Each stage produces a specific deliverable. The stages are sequential in logic and iterative in practice — the loop compounds as the operating method takes hold.
          </p>
        </div>
        <StepperBlock
          orientation="vertical"
          steps={[
            { num: "01", title: "Engagement Inquiry", body: "The engagement begins with an inquiry through the contact channels — specifying the organization, the operational challenge, and the engagement preferences. The inquiry is the first diagnostic step: providing the information necessary to scope the engagement. Every inquiry receives a response grounded in the same operational discipline that governs all work.", accent: "red" },
            { num: "02", title: "Diagnostic Conversation", body: "A 60 to 90 minute conversation with organizational leadership to map the operational landscape. The diagnostic produces a comprehensive report identifying gaps, root causes, systemic patterns, and recommended interventions. No consulting is prescribed without diagnosis. No scope is defined without understanding the landscape.", accent: "purple" },
            { num: "03", title: "Engagement Design", body: "Based on the diagnostic, the consulting design specifies the scope, methodology, milestones, deliverable specifications, and outcome metrics. The design is grounded in diagnostic data, not in a predetermined service template. Each engagement design is calibrated to the specific operational requirements.", accent: "cyan" },
            { num: "04", title: "Intelligence and Analysis", body: "Forensic examination of the organizational landscape — strategic objectives, operational capabilities, governance structures, execution gaps, cultural dynamics, technology infrastructure, and stakeholder ecosystem. The intelligence produces the evidence base for the intervention design.", accent: "emerald" },
            { num: "05", title: "Implementation Blueprint", body: "The blueprint specifies the operating mechanisms the organization will deploy: protocols, checklists, decision frameworks, accountability structures, and governance mechanisms. The blueprint is not a vision document. It is an implementation specification with clinical precision.", accent: "amber-gold" },
            { num: "06", title: "Orchestrated Transformation", body: "The consulting produces transformation through coordinated implementation across all relevant teams and systems. Every team aligned, every protocol deployed, every accountability structure activated. The transformation is measured at every stage against the diagnostic benchmarks — outcomes verified, not assumed.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Cpu className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Consulting by the Numbers</span>
          </div>
          <h2 className="text-display-md mb-4">Verified outcomes, measured over time.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The operational consulting inherits the operational standard — outcomes measured, not assumed. The metrics below are drawn from the same infrastructure that supports sovereign-scale operations.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 99.9999, suffix: "%", label: "Operational Uptime", description: "Maximum downtime of 31.5 seconds per year — the standard the consulting installs.", accent: "red" },
            { value: 83.3, suffix: "%", label: "Campaign Success", description: "Sovereign-level campaign success rate across political and corporate engagements.", accent: "purple" },
            { value: 100, suffix: "%", label: "Client Satisfaction", description: "Verified satisfaction across more than three hundred elite clients served.", accent: "cyan" },
            { value: 0, label: "Security Incidents", description: "Zero incidents across fifteen-plus years of sovereign-level engagements.", accent: "emerald" },
            { value: 18, label: "Countries", description: "Operational reach across three continents with sovereign-grade deployments.", accent: "amber-gold" },
            { value: 900, suffix: "M+", label: "Citizens Reached", description: "Population reach across governance and operational deployments worldwide.", accent: "denim" },
            { value: 70, suffix: "%+", label: "Women in Teams", description: "Average composition across operational teams — the 70% Operating Model validated.", accent: "lime" },
            { value: 15, suffix: "+", label: "Years Operational", description: "Continuous operation with the same outcome accountability standard.", accent: "red" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Compass className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Questions organizations ask before engaging consulting.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Operational consulting is curated. The diagnostic conversation addresses what the FAQ cannot — and produces the answer to questions organizations have not yet formulated.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "What is the difference between operational consulting and management consulting?", a: "Most management consulting produces recommendations — documents that describe what should happen. Operational consulting produces transformation — verified changes in how the organization actually operates. The distinction is structural. A recommendation is a possibility. A transformation is a measured outcome." },
            { q: "How long does a typical consulting engagement last?", a: "Project engagements run from three to twelve months depending on scope. Retainer engagements continue as long as the organization benefits from sustained implementation. Intensive engagements are concentrated over three to five days. Duration is calibrated to the diagnostic data." },
            { q: "Is the consulting available by introduction only?", a: "Operational consulting is curated, not marketed. Engagements are evaluated based on the organization's mission, the operational challenge, and the alignment with operational expertise. The introduction model ensures the diagnostic conversation can proceed with the trust and discretion sovereign-level work demands." },
            { q: "What outcomes can the organization expect?", a: "The organization gains verified changes in execution speed, operational uptime, governance effectiveness, and organizational resilience. The deliverable is operating infrastructure — protocols, checklists, decision frameworks, accountability structures — that persist beyond the engagement." },
            { q: "How does the consulting handle confidentiality?", a: "National security level confidentiality governs every engagement. Binding non-disclosure agreements are tailored to the specific sensitivity level. The compartmentalized architecture ensures every participant sees only what they need to see. The infrastructure that took a decade to build supports every engagement." },
            { q: "Can the consulting combine with other services?", a: "Yes. Consulting frequently combines with executive advisory for ongoing leadership development, corporate workshops for team-level implementation, and executive mentorship for individual leader development. The combinations are designed around the diagnostic data." },
            { q: "What is the consulting fee structure?", a: "Fees are discussed during the diagnostic phase, tailored to the engagement model, scope, duration, and outcome requirements. The diagnostic conversation determines scope and fee structure simultaneously. Every consulting engagement is customized — there are no standard packages." },
            { q: "How is success measured?", a: "Outcomes are measured against benchmarks established during the diagnostic phase. The metrics include execution speed improvement, operational uptime, governance effectiveness, and organizational resilience. The measurement framework mirrors the accountability standard that governs medical practice." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Activity className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Inquire about operational consulting.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Operational consulting engagements are available by introduction. The diagnostic conversation determines whether the engagement is appropriate — whether the organization&apos;s challenges align with operational expertise and whether the consulting can produce the verified outcomes that both parties require.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Inquire About Consulting
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/private-engagements/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Lock className="size-4" />
                Private Engagements
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Private Engagements", description: "Sovereign-level engagements by introduction only. Absolute discretion.", href: "/services/private-engagements/", icon: Lock, accent: "denim" },
          { title: "Corporate Workshops", description: "Intensive team training on the operating method — systems, not slide decks.", href: "/services/corporate-workshops/", icon: Layers, accent: "lime" },
          { title: "Board Service", description: "Sovereign-grade governance oversight at the board level.", href: "/services/board-service/", icon: Building2, accent: "cyan" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
